/**
 * useCityProximity - Detect if user is near known city pages.
 * Returns nearby cities within threshold km, sorted by distance.
 * Each city can be individually dismissed (persisted to sessionStorage).
 * When a city is dismissed the next closest replaces it in the list.
 * @atomic @idempotent
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { cityAPI } from '../services/api'
import { createLogger } from '../utils/logger'

const logger = createLogger('useCityProximity')

const THRESHOLD_KM = 60
const DISMISS_KEY = 'city_proximity_dismissed_v2'
const CACHE_KEY = 'city_list_cache_v1'
const CACHE_TTL_MS = 60 * 60 * 1000 // 1 hour

function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function getCachedCities() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const { ts, data } = JSON.parse(raw)
    if (Date.now() - ts > CACHE_TTL_MS) return null
    return data
  } catch {
    return null
  }
}

function setCachedCities(cities) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: cities }))
  } catch { /* storage full or unavailable */ }
}

function getDismissedSlugs() {
  try {
    const raw = sessionStorage.getItem(DISMISS_KEY)
    return raw ? new Set(JSON.parse(raw)) : new Set()
  } catch {
    return new Set()
  }
}

function saveDismissedSlugs(set) {
  try {
    sessionStorage.setItem(DISMISS_KEY, JSON.stringify([...set]))
  } catch { /* noop */ }
}

/**
 * @returns {{ nearbyCities: Array<{city: object, distanceKm: number}>, dismiss: (slug: string) => void, ready: boolean }}
 */
export default function useCityProximity() {
  const [nearbyCities, setNearbyCities] = useState([])
  const [ready, setReady] = useState(false)
  const debounceRef = useRef(null)
  const lastCoordsRef = useRef(null)
  const allCitiesRef = useRef(null)

  const findNearby = useCallback((lat, lon, cities) => {
    const dismissed = getDismissedSlugs()
    const results = []
    for (const city of cities) {
      if (!city.is_active && city.is_active !== undefined) continue
      if (dismissed.has(city.slug)) continue
      const d = haversineKm(lat, lon, Number(city.latitude), Number(city.longitude))
      if (d <= THRESHOLD_KM) results.push({ city, distanceKm: Math.round(d) })
    }
    results.sort((a, b) => a.distanceKm - b.distanceKm)
    setNearbyCities(results)
    setReady(true)
  }, [])

  useEffect(() => {
    if (!navigator.geolocation) { setReady(true); return }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords
        clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(async () => {
          try {
            let cities = getCachedCities()
              if (!cities) {
                const res = await cityAPI.getCities()
                cities = res.data?.cities || res.data || []
                if (cities.length) setCachedCities(cities)
              }
              lastCoordsRef.current = { latitude, longitude }
              allCitiesRef.current = cities
              findNearby(latitude, longitude, cities)
          } catch (err) {
            logger.error('City proximity lookup failed', err)
            setReady(true)
          }
        }, 500)
      },
      () => { setReady(true) }, // permission denied or error — silent
      { timeout: 8000, maximumAge: 5 * 60 * 1000 }
    )

    return () => clearTimeout(debounceRef.current)
  }, [findNearby])

  const dismiss = useCallback((slug) => {
    const dismissed = getDismissedSlugs()
    dismissed.add(slug)
    saveDismissedSlugs(dismissed)
    setNearbyCities((prev) => prev.filter((item) => item.city.slug !== slug))
  }, [])

  const resetDismissed = useCallback(() => {
    try { sessionStorage.removeItem(DISMISS_KEY) } catch { /* noop */ }
    const { latitude, longitude } = lastCoordsRef.current || {}
    if (latitude != null && allCitiesRef.current) {
      findNearby(latitude, longitude, allCitiesRef.current)
    }
  }, [findNearby])

  return { nearbyCities, dismiss, resetDismissed, ready }
}
